import { TENANTS } from "@ow/shared";
import { type CSSProperties, useState } from "react";
import { iso3to2 } from "./helpers";
import { C } from "./tokens";

/** ISO3 of the demo tenant's invented country — the one code no flag CDN has. */
const DEMO_ISO3 = TENANTS.wonderland.iso3;

// Image-based country flag. Replaces the old Unicode regional-indicator
// emoji (flagEmoji) which renders as bare letters ("FR") on Windows/Chrome
// and many Linux fonts — emoji flags simply aren't in those font stacks.
// Pulls 4:3 PNGs from flagcdn.com (same CDN posture as the d3 world.geojson
// fetch).
//
// Falls back to a neutral 2-letter chip when we have no ISO mapping OR the CDN
// image fails to load (onError): flagcdn has no image for some territories
// — Bouvet, Heard, the Antarctic dependencies, etc. — and a transient network
// failure shouldn't leave a blank gap. The chip is sized to the flag box so
// the surrounding layout never shifts.
export function Flag({ iso3, width = 18, style }: { iso3: string; width?: number; style?: CSSProperties }) {
  const iso2 = iso3to2[iso3]?.toLowerCase();
  const [failed, setFailed] = useState(false);
  const height = Math.round(width * 0.75);
  // The demo tenant's focus country is not a real place, so flagcdn has nothing
  // for it and the neutral chip below would render beside every real flag on
  // the screen. Draw it instead — the demo has to read as a country.
  if (iso3 === DEMO_ISO3) {
    return <DemoFlag width={width} height={height} style={style} />;
  }
  if (!iso2 || failed) {
    return (
      <span
        aria-hidden="true"
        title={iso3}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width,
          height,
          fontSize: Math.round(width * 0.46),
          fontWeight: 600,
          letterSpacing: "0.02em",
          color: C.textLo,
          background: C.panelAlt,
          border: `0.5px solid ${C.border}`,
          borderRadius: 2,
          verticalAlign: "middle",
          overflow: "hidden",
          ...style,
        }}
      >
        {iso3.slice(0, 2)}
      </span>
    );
  }
  return (
    <img
      src={`https://flagcdn.com/48x36/${iso2}.png`}
      srcSet={`https://flagcdn.com/96x72/${iso2}.png 2x`}
      alt=""
      width={width}
      height={height}
      loading="lazy"
      onError={() => setFailed(true)}
      style={{
        display: "inline-block",
        objectFit: "cover",
        borderRadius: 2,
        boxShadow: "0 0 0 0.5px rgba(0,15,71,0.12)",
        verticalAlign: "middle",
        ...style,
      }}
    />
  );
}

/**
 * Flag artwork for the demo tenant's country: a crimson field, a gold hoist band
 * and a gold sun. Drawn at the same 4:3 box, border-radius and hairline as the
 * CDN images so it sits in a list of real flags without standing out.
 */
function DemoFlag({ width, height, style }: { width: number; height: number; style?: CSSProperties }) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 36 27"
      aria-hidden="true"
      style={{
        display: "inline-block",
        borderRadius: 2,
        boxShadow: "0 0 0 0.5px rgba(0,15,71,0.12)",
        verticalAlign: "middle",
        flexShrink: 0,
        ...style,
      }}
    >
      <rect width={36} height={27} fill="#9E1B32" />
      <rect width={7} height={27} fill="#D9A441" />
      <circle cx={22.5} cy={13.5} r={5.4} fill="#D9A441" />
      <circle cx={22.5} cy={13.5} r={3.4} fill="#9E1B32" />
    </svg>
  );
}
